/**
 * Thin wrappers for the public self-service form endpoints.
 *
 * The backend surface is intentionally unauthenticated REST (not GraphQL):
 *  - GET  /public/forms/:token                 → returns the form render data
 *  - POST /public/forms/:token/submit          → creates a document record
 *  - POST /public/forms/:token/lookup          → proxied masterdata search
 *  - GET  /public/forms/request/:token         → returns the request page render data
 *  - POST /public/forms/request/:token/submit  → creates a form client request
 *
 * Apollo is not involved on this route; we do not want the auth-cookie-bearing
 * GraphQL client accidentally used by a public customer.
 */

import axios from 'axios';

export interface FormOption {
    name: string;
    value: unknown;
    value_type: string;
    default_value: unknown;
}

export interface FormField {
    name: string;
    value_type: string;
    position: number | null;
    enabled: boolean;
    options: FormOption[];
}

export interface FormRenderData {
    form: {
        id: string;
        token: string;
        intro_text: string;
        client: {
            customer_number: string;
            name: string;
        };
    };
    channel: {
        id: string;
        name: string;
        document_type: string;
    };
    tenant: {
        name: string;
        logo_url: string | null;
    };
    fields: FormField[];
}

export interface SubmitResponse {
    record_id: string;
    reference_number: string;
}

export interface FormRequestRenderData {
    form: {
        id: string;
        token: string;
        name: string;
        intro_text: string;
    };
    channel: {
        id: string;
        name: string;
    };
    tenant: {
        name: string;
        logo_url: string | null;
    };
}

const BACKEND_URL = process.env.BACKEND_URL || '';

export const fetchFormRenderData = async (token: string, signal?: AbortSignal): Promise<FormRenderData> => {
    const { data } = await axios.get(`${BACKEND_URL}public/forms/${encodeURIComponent(token)}`, { signal });
    return data;
};

export const submitForm = async (
    token: string,
    payload: { sender_email: string; header: Record<string, unknown>; line_items: Record<string, unknown>[] }
): Promise<SubmitResponse> => {
    const { data } = await axios.post(`${BACKEND_URL}public/forms/${encodeURIComponent(token)}/submit`, payload);
    return data;
};

export const fetchFormRequestRenderData = async (
    token: string,
    signal?: AbortSignal
): Promise<FormRequestRenderData> => {
    const { data } = await axios.get(`${BACKEND_URL}public/forms/request/${encodeURIComponent(token)}`, { signal });
    return data;
};

export const submitFormRequest = async (
    token: string,
    payload: { email: string; phone: string; note: string; language: string }
): Promise<void> => {
    await axios.post(`${BACKEND_URL}public/forms/request/${encodeURIComponent(token)}/submit`, payload);
};

/**
 * Call the public masterdata lookup proxy. The backend validates the form
 * token, checks the `lookupDefinitionId` against the channel's configured
 * lookup ids, and forwards the same GraphQL query the Assistance UI uses.
 * The response shape matches `assistanceSearchByField` — i.e. `edges[].node`.
 */
export interface LookupSearchVariables {
    lookupDefinitionId: string;
    lookupFieldName: string;
    searchTerm: string;
    partitionId?: string | null;
    from?: number;
    size?: number;
}

export const searchLookup = async (
    token: string,
    variables: LookupSearchVariables
): Promise<{ edges: { node: Record<string, unknown> }[] }> => {
    try {
        const { data } = await axios.post(`${BACKEND_URL}public/forms/${encodeURIComponent(token)}/lookup`, {
            variables: {
                lookupDefinitionId: variables.lookupDefinitionId,
                targetField: variables.lookupFieldName,
                searchTerm: variables.searchTerm,
                partitionId: variables.partitionId ?? null,
                from: variables.from ?? 0,
                size: variables.size ?? 10,
            },
        });
        return data?.data?.assistanceSearchByField ?? { edges: [] };
    } catch {
        return { edges: [] };
    }
};
